import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage, formatPLN } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Send, Plus, Sparkle, ShoppingCart } from "lucide-react";

export default function CustomProductRequestPage() {
  const [form, setForm] = useState({ product_name: "", estimated_quantity: 1, product_link: "", comment: "", suggest_add_to_catalog: false });
  const [submitting, setSubmitting] = useState(false);
  const [quoted, setQuoted] = useState([]);
  const { addItem, count, total } = useCart();

  const loadQuoted = () => api.get("/custom-product-requests/quoted").then((r) => setQuoted(r.data || []));
  useEffect(() => { loadQuoted(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/custom-product-requests", { ...form, estimated_quantity: Number(form.estimated_quantity) });
      toast.success("Zapytanie wysłane do kierownika i Office-Land");
      setForm({ product_name: "", estimated_quantity: 1, product_link: "", comment: "", suggest_add_to_catalog: false });
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const addQuoted = (q) => {
    addItem(
      {
        id: `custom:${q.id}`,
        custom_request_id: q.id,
        name: q.product_name,
        sku: `CUSTOM-${q.id.slice(0, 8).toUpperCase()}`,
        unit: "szt.",
        price_net: q.estimated_price_net,
        category: "Wycena Office-Land",
      },
      1,
    );
    toast.success(`Dodano wycenę: ${q.product_name}`);
  };

  return (
    <div>
      <PageHeader
        testId="custom-request-page"
        title="Zapytaj o produkt spoza katalogu"
        subtitle="Brakuje czegoś w katalogu? Wyślij zapytanie do kierownika i Office-Land."
        actions={
          <Link
            to="/cart"
            data-testid="custom-cart-link"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors"
          >
            <ShoppingCart className="w-4 h-4" /> Koszyk · {count} poz. · {formatPLN(total)}
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="ol-card p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Nazwa produktu *</label>
            <input
              required
              value={form.product_name}
              onChange={(e) => setForm({ ...form, product_name: e.target.value })}
              data-testid="custom-name"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Szacowana ilość *</label>
            <input
              required type="number" min="1" step="1"
              value={form.estimated_quantity}
              onChange={(e) => setForm({ ...form, estimated_quantity: e.target.value })}
              data-testid="custom-qty"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Link do produktu (opcjonalnie)</label>
            <input
              type="text"
              value={form.product_link}
              onChange={(e) => setForm({ ...form, product_link: e.target.value })}
              data-testid="custom-link"
              placeholder="np. sklep.pl/produkt — https:// nie jest wymagane"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">Komentarz</label>
            <textarea
              rows={3}
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              data-testid="custom-comment"
              className="w-full px-3 py-2 rounded-md border border-slate-300 focus:border-[#FF6B1A] focus:ring-2 focus:ring-[#FF6B1A]/30 focus:outline-none text-sm"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md border border-slate-200 hover:border-[#FF6B1A] transition-colors bg-slate-50/40">
            <input
              type="checkbox"
              checked={form.suggest_add_to_catalog}
              onChange={(e) => setForm({ ...form, suggest_add_to_catalog: e.target.checked })}
              data-testid="suggest-add-catalog"
              className="mt-0.5 w-4 h-4 accent-[#FF6B1A]"
            />
            <div>
              <p className="text-sm font-semibold text-slate-900">Sugeruję dodać do katalogu na stałe</p>
              <p className="text-xs text-slate-500 mt-0.5">Zaznacz jeśli prawdopodobnie będziesz zamawiać ten produkt regularnie. Kierownik i Office-Land zobaczą Twoją sugestię.</p>
            </div>
          </label>
          <button type="submit" disabled={submitting} data-testid="custom-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold transition-colors disabled:opacity-60">
            <Send className="w-4 h-4" /> Wyślij zapytanie
          </button>
        </form>

        <div className="ol-card overflow-hidden border-2 border-[#FF6B1A]/30 bg-gradient-to-br from-[#FFF4ED] to-white" data-testid="quoted-list">
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="text-xs font-bold uppercase tracking-widest text-[#CC4B00] flex items-center gap-1.5">
              <Sparkle className="w-3.5 h-3.5" /> Wycenione przez Office-Land
            </p>
            <p className="text-xs text-slate-600 mt-1">
              {quoted.length === 0
                ? "Brak wycenionych zapytań. Wyślij zapytanie z lewej strony — Office-Land wyceni i pojawi się tutaj."
                : `${quoted.length} ${quoted.length === 1 ? "produkt" : "produktów"} z wyceną Office-Land — możesz dodać do zamówienia jednym kliknięciem.`}
            </p>
          </div>
          {quoted.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400 italic">
              Brak wycen
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {quoted.map((q) => (
                <div key={q.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#FFF4ED]/60 transition-colors" data-testid={`quoted-row-${q.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{q.product_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cena netto: <strong className="text-[#CC4B00]">{formatPLN(q.estimated_price_net)}</strong>
                      {q.estimated_quantity ? <span className="text-slate-400"> · oryg. ilość: {q.estimated_quantity}</span> : null}
                    </p>
                    {q.office_land_comment && (
                      <p className="text-[11px] text-slate-500 mt-0.5 italic">&bdquo;{q.office_land_comment}&rdquo;</p>
                    )}
                  </div>
                  <button
                    onClick={() => addQuoted(q)}
                    data-testid={`add-quoted-list-${q.id}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-xs font-semibold flex-shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" /> Dodaj
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
